import React, { useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiArrowRight, FiMinus, FiPlus, FiTrash2 } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData, { siteConfig } from "../Datas/productData";
import "../css/cartpage.scss";

function CartPage() {
  const navigate = useNavigate();
  const { cartItems, clearCart, removeFromCart, updateQuantity } = useContext(CartContext);
  const [promoCode, setPromoCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [promoMessage, setPromoMessage] = useState("");

  const resolvedItems = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = resolvedItems.reduce(
    (total, item) => total + item.product.price * item.quantity,
    0
  );
  const discountAmount = subtotal * discount;
  const discountedSubtotal = subtotal - discountAmount;
  const shipping = discountedSubtotal >= siteConfig.freeShippingThreshold ? 0 : siteConfig.shippingFlatRate;
  const total = discountedSubtotal + shipping;

  const applyPromo = (event) => {
    event.preventDefault();
    if (promoCode.trim().toUpperCase() === "WORKSHOP10") {
      setDiscount(0.1);
      setPromoMessage("Workshop code applied — 10% off.");
    } else {
      setDiscount(0);
      setPromoMessage("That code isn’t active. Try WORKSHOP10 for this demo.");
    }
  };

  const handleCheckout = () => {
    navigate("/checkout", { state: { discount } });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (resolvedItems.length === 0) {
    return (
      <main className="cart-page cart-page--empty">
        <span className="eyebrow">Your cart</span>
        <h1>Your cart is empty.</h1>
        <p>Start with a pair of raw denim, then build the rest around it.</p>
        <Link className="button button--accent" to="/shop">
          Shop the collection <FiArrowRight aria-hidden="true" />
        </Link>
      </main>
    );
  }

  return (
    <main className="cart-page page-width">
      <header className="cart-page__header">
        <div>
          <span className="eyebrow">Shopping bag</span>
          <h1>Your cart</h1>
        </div>
        <button type="button" onClick={clearCart}>Clear cart</button>
      </header>

      <div className="cart-page__layout">
        <section className="cart-page__items" aria-label="Cart items">
          <div className="cart-page__table-head">
            <span>Product</span><span>Price</span><span>Quantity</span><span>Total</span>
          </div>
          {resolvedItems.map((item) => {
            const lineTotal = item.product.price * item.quantity;
            return (
              <article className="cart-item" key={`${item.id}-${item.size}-${item.color}`}>
                <Link className="cart-item__image" to={`/shopDetails/${item.product.id}`}>
                  <img src={item.product.image} alt={item.product.name} width="1086" height="1448" />
                </Link>
                <div className="cart-item__info">
                  <span>{item.product.category}</span>
                  <h2><Link to={`/shopDetails/${item.product.id}`}>{item.product.name}</Link></h2>
                  <p>{item.color} · Size {item.size}</p>
                  <button
                    type="button"
                    onClick={() => removeFromCart(item.id, item.size, item.color)}
                  >
                    <FiTrash2 aria-hidden="true" /> Remove
                  </button>
                </div>
                <div className="cart-item__unit-price">
                  <small>Price</small>{siteConfig.currencySymbol}{item.product.price.toFixed(2)}
                </div>
                <div className="cart-item__quantity">
                  <small>Quantity</small>
                  <div>
                    <button
                      type="button"
                      onClick={() => updateQuantity(item.id, item.size, item.color, item.quantity - 1)}
                      disabled={item.quantity === 1}
                      aria-label={`Decrease ${item.product.name} quantity`}
                    ><FiMinus /></button>
                    <span>{item.quantity}</span>
                    <button
                      type="button"
                      onClick={() => updateQuantity(item.id, item.size, item.color, item.quantity + 1)}
                      aria-label={`Increase ${item.product.name} quantity`}
                    ><FiPlus /></button>
                  </div>
                </div>
                <div className="cart-item__line-total">
                  <small>Total</small>{siteConfig.currencySymbol}{lineTotal.toFixed(2)}
                </div>
              </article>
            );
          })}
          <Link className="cart-page__continue" to="/shop">
            <FiArrowLeft aria-hidden="true" /> Continue shopping
          </Link>
        </section>

        <aside className="cart-page__summary">
          <span className="eyebrow">Order summary</span>
          <h2>Ready when you are.</h2>
          <form onSubmit={applyPromo}>
            <label htmlFor="promo-code">Promo code</label>
            <div>
              <input
                id="promo-code"
                value={promoCode}
                onChange={(event) => setPromoCode(event.target.value)}
                placeholder="Enter code"
              />
              <button type="submit">Apply</button>
            </div>
            {promoMessage && <p role="status">{promoMessage}</p>}
          </form>
          <dl>
            <div><dt>Subtotal</dt><dd>{siteConfig.currencySymbol}{subtotal.toFixed(2)}</dd></div>
            {discount > 0 && <div className="is-discount"><dt>Discount</dt><dd>−{siteConfig.currencySymbol}{discountAmount.toFixed(2)}</dd></div>}
            <div><dt>Shipping</dt><dd>{shipping === 0 ? "Free" : `${siteConfig.currencySymbol}${shipping.toFixed(2)}`}</dd></div>
            <div className="cart-page__summary-total"><dt>Total <small>{siteConfig.currency}</small></dt><dd>{siteConfig.currencySymbol}{total.toFixed(2)}</dd></div>
          </dl>
          <p className="cart-page__shipping-copy">
            Taxes are calculated at checkout. Free U.S. delivery applies after discounts.
          </p>
          <button className="button button--accent" type="button" onClick={handleCheckout}>
            Checkout <FiArrowRight aria-hidden="true" />
          </button>
          <div className="cart-page__assurances">
            <span>Secure checkout</span><span>30-day returns</span><span>Built to last</span>
          </div>
        </aside>
      </div>
    </main>
  );
}

export default CartPage;
